import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import EventDetailsInteractive from './components/EventDetailsInteractive';

const EventDetailsPage = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const [status, setStatus] = useState({ loading: true, error: '', invite: null });

  useEffect(() => {
    const load = async () => {
      if (!id) {
        setStatus({ loading: false, error: '잘못된 접근입니다. 이벤트 ID가 없습니다.', invite: null });
        return;
      }

      setStatus({ loading: true, error: '', invite: null });
      try {
        const { data, error } = await supabase
          .from('session_invites')
          .select('*')
          .eq('id', id)
          .maybeSingle();

        if (error) {
          setStatus({ loading: false, error: error?.message || '이벤트 정보를 불러오지 못했습니다.', invite: null });
          return;
        }

        if (!data) {
          setStatus({ loading: false, error: '이벤트 정보를 찾을 수 없습니다.', invite: null });
          return;
        }

        setStatus({ loading: false, error: '', invite: data });
      } catch {
        setStatus({ loading: false, error: '이벤트 정보를 불러오는 중 오류가 발생했습니다.', invite: null });
      }
    };

    load();
  }, [id]);

  const mapped = useMemo(() => {
    const invite = status.invite;
    if (!invite) return null;

    const rawParticipants = Array.isArray(invite?.participants) ? invite.participants : [];
    const confirmed = rawParticipants.filter((p) => (p?.status || 'pending') === 'confirmed').length;

    const participants = rawParticipants
      .filter((p) => (p?.name || p?.nickname || '').toString().trim())
      .map((p) => {
        const id = String(p?.id || globalThis.crypto?.randomUUID?.() || String(Date.now()));
        const name = String(p?.nickname || p?.name || '').trim() || '참가자';
        const registeredAt = String(p?.registeredAt || p?.registered_at || '');
        const joinedDate = registeredAt ? new Date(registeredAt).toLocaleDateString('ko-KR') : '';
        return {
          id,
          name,
          avatar: 'https://img.rocket.new/generatedImages/rocket_gen_img_14005a748-1763298682933.png',
          avatarAlt: 'User avatar',
          skillLevel: String(p?.skillLevel || p?.skill_level || '').trim(),
          joinedDate,
        };
      });

    const eventData = {
      id: invite.id,
      token: invite.token,
      sportType: '오픈플레이',
      sportIcon: 'TrophyIcon',
      organizationName: invite?.title || '이벤트',
      organizationLogo: 'https://img.rocket.new/generatedImages/rocket_gen_img_144685795-1768042270006.png',
      organizationLogoAlt: 'Pickling',
      coverImageUrl: String(invite?.cover_image_url || '').trim(),
      imageUrls: Array.isArray(invite?.image_urls) ? invite.image_urls : [],
      location: invite?.venue || '',
      currentParticipants: confirmed,
      maxParticipants: invite?.capacity ?? 0,
      rating: 0,
      totalEvents: 0,
      date: invite?.date ? new Date(invite.date).toLocaleDateString('ko-KR') : '',
      time: `${invite?.start_time || ''}${invite?.end_time ? ` - ${invite.end_time}` : ''}`.trim(),
      duration: '',
      skillLevel: '',
      description: '',
      facilityInfo: '',
      equipment: [],
      chatRoomUrl: '',
    };

    return {
      eventData,
      participants,
      sessionInviteId: invite.id,
      token: invite.token,
      inquiries: [],
    };
  }, [status.invite]);

  if (status.loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-sm text-muted-foreground">로딩 중...</div>
      </div>
    );
  }

  if (status.error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-card border border-border rounded-xl p-6 text-center">
          <p className="text-base font-semibold text-foreground mb-2">이벤트를 불러올 수 없습니다</p>
          <p className="text-sm text-muted-foreground caption">{status.error}</p>
        </div>
      </div>
    );
  }

  if (!mapped) return null;

  return (
    <EventDetailsInteractive
      eventData={mapped.eventData}
      participants={mapped.participants}
      inquiries={mapped.inquiries}
      sessionInviteId={mapped.sessionInviteId}
      token={mapped.token}
    />
  );
};

export default EventDetailsPage;
